import { type FC } from 'react'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import { inAppChatService } from '@egw/app-chat'
import { useGetEnrollmentHYCSManualReviewData } from '@egw/api'
import { Button, Alert, AlertType, GenericError } from '@egw/components'
import { useTranslation } from '@egw/i18n'
import { Logger } from '@egw/logger'
import { EnrollmentHYCSLayout } from '@egw/layouts'
import { sendEvent } from '@egw/events'
import { PageHeader } from '../../../components'
import { useValidateRoute } from '../../../hooks/navigation/validate-route'
import { EDataLayerEventWealth } from '../../../constants/events/EDataLayerEventWealth'

const log = new Logger('Enrollment - HYCS - ManualReview')

export const ManualReviewPage: FC = () => {
  const { t } = useTranslation('features-enrollment')

  const { data, isLoading, error } = useGetEnrollmentHYCSManualReviewData()

  const applicationId = data?.__typename === 'SubmitEnrollmentSuccess' ? data.enrollmentData?.applicationId : ''

  // Protect route
  useValidateRoute(data?.path)

  return (
    <EnrollmentHYCSLayout title={t('hycs.manual-review.page_title')}>
      <div className="egw-container-enroll-hycs h-full flex flex-col relative md:justify-center justify-start items-start">
        {/* Loading initial data to make sure the user has access to this screen and retreive data based on user enrollment application */}
        {isLoading ? (
          <SkeletonTheme baseColor="#eeefee" highlightColor="rgba(255, 255, 255, 0.5)">
            <Skeleton count={1} height="100px" className="mb-10" />
            <Skeleton count={5} height="50px" />
          </SkeletonTheme>
        ) : null}

        {/* In some cases initial data can be null, so we assume if it's not loading and there's no error - we show the page */}
        {!isLoading && !error ? (
          <div className="w-full flex-1 flex flex-col justify-start lg:justify-center">
            <PageHeader title={t('hycs.manual-review.title')} />
            <Alert alertType={AlertType.Warning} description={t('hycs.manual-review.alert')} className="mb-xl mt-sm" />
            <p className="typo-primary-base-regular text-text-body">{t('hycs.manual-review.description_1')}</p>
            <p className="typo-primary-base-regular text-text-body mt-md">{t('hycs.manual-review.description_2')}</p>

            <div className="flex-1 md:hidden" />

            {/* AUTO GENERATED COMPONENTS */}

            <Button
              type="submit"
              className="w-full md:w-min mt-xl"
              testId="primaryAction_button"
              onClick={() => {
                sendEvent(EDataLayerEventWealth.BANKHYCHYS_MANUALREVIEW_STARTED, {
                  applicationId,
                })
                inAppChatService.open()
              }}
            >
              {t('hycs.manual-review.start_chat')}
            </Button>
          </div>
        ) : null}

        {/* This error handles the case when the initial data is not loaded, for example network error */}
        {error ? (
          <div className="flex-1 flex flex-col justify-start md:justify-center">
            <GenericError errorMessage={t('common.generic_error')} />
          </div>
        ) : null}
      </div>
    </EnrollmentHYCSLayout>
  )
}
